import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faUserCheck, 
  faUserTimes, 
  faBuilding, 
  faHome, 
  faCalendarCheck, 
  faChartLine,
  faAddressCard
} from '@fortawesome/free-solid-svg-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import Api from '../../Api';
import { toast } from 'react-toastify';
import './style.css'; 

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);

  const api = Api();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response: any = await api.dashboard.getStats();
      if (response && response.result) {
        setStats(response.result.users ? response.result : response.result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <h5 className="mt-3 text-muted fw-bold">Loading Dashboard...</h5>
        </div>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center bg-white p-5 rounded-4 shadow-sm border border-danger">
          <h4 className="text-danger fw-bold mb-3">Failed to load statistics</h4>
          <p className="text-muted">Please try refreshing the page or check the backend server connection.</p>
        </div>
      </Container>
    );
  }

  // Data for Charts
  const userPieData = [
    { name: 'Active Users', value: stats.users.active, color: '#1cc88a' },
    { name: 'Inactive Users', value: stats.users.inactive, color: '#e74a3b' }
  ];

  const propertyPieData = [
    { name: 'Active Properties', value: stats.properties.active, color: '#20c997' },
    { name: 'Unverified Properties', value: stats.properties.total - stats.properties.active, color: '#fd7e14' }
  ];

  const barData = [
    { name: 'Users', Total: stats.users.total, Verified: stats.users.verified },
    { name: 'Properties', Total: stats.properties.total, Verified: stats.properties.verified }
  ];

  return (
    <Container fluid className="dashboard-container py-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FontAwesomeIcon icon={faChartLine as any} className="me-2 text-primary" />
            Interactive Dashboard
          </h2>
          <p className="text-muted mb-0">Live statistics and insights for Cirayedaar.</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Badge bg="primary" className="px-3 py-2 rounded-pill shadow-sm fs-6 pulse-badge">
            <FontAwesomeIcon icon={faCalendarCheck as any} className="me-2" />
            {stats.engagement.appointmentRequests} Leads
          </Badge>
          <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm fs-6">
            System Online
          </Badge>
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col xl={8}>
          <h5 className="fw-bold text-secondary mb-3">Key Metrics Overview</h5>
          <Row className="g-3">
            <Col lg={4} md={6}>
              <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-primary text-white interactive-card">
                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold fs-5">Total Users</p>
                      <h2 className="display-6 fw-bold mb-0 count-up">{stats.users.total}</h2>
                    </div>
                    <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                      <FontAwesomeIcon icon={faUsers as any} size="xl" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={4} md={6}>
              <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-success text-white interactive-card">
                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold fs-5">Active Users</p>
                      <h2 className="display-6 fw-bold mb-0 count-up">{stats.users.active}</h2>
                    </div>
                    <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                      <FontAwesomeIcon icon={faUserCheck as any} size="xl" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-info text-white interactive-card">
                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold fs-5">Verified Profiles</p>
                      <h2 className="display-6 fw-bold mb-0 count-up">{stats.users.verified}</h2>
                    </div>
                    <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                      <FontAwesomeIcon icon={faAddressCard as any} size="xl" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-purple text-white interactive-card">
                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold fs-5">Total Properties</p>
                      <h2 className="display-6 fw-bold mb-0 count-up">{stats.properties.total}</h2>
                    </div>
                    <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                      <FontAwesomeIcon icon={faBuilding as any} size="xl" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={4} md={6}>
              <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-teal text-white interactive-card">
                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold fs-5">Active Properties</p>
                      <h2 className="display-6 fw-bold mb-0 count-up">{stats.properties.active}</h2>
                    </div>
                    <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                      <FontAwesomeIcon icon={faHome as any} size="xl" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="dashboard-card border-0 shadow-sm h-100 bg-gradient-orange text-white interactive-card">
                <Card.Body className="p-4 d-flex flex-column justify-content-between">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-semibold opacity-75">Verified Properties</p>
                      <h2 className="display-6 fw-bold mb-0 count-up">{stats.properties.verified}</h2>
                    </div>
                    <div className="icon-wrapper bg-white bg-opacity-25 rounded-circle p-3 shadow-sm">
                      <FontAwesomeIcon icon={faBuilding as any} size="xl" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col xl={4}>
          <h5 className="fw-bold text-secondary mb-3">User Distribution</h5>
          <Card className="border-0 shadow-sm h-100 chart-card">
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {userPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-5">
        <Col xl={6}>
          <h5 className="fw-bold text-secondary mb-3">Verification Comparison</h5>
          <Card className="border-0 shadow-sm chart-card">
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip cursor={{fill: 'rgba(0,0,0,0.05)'}} />
                  <Legend />
                  <Bar dataKey="Total" fill="#4e73df" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Verified" fill="#1cc88a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={6}>
          <h5 className="fw-bold text-secondary mb-3">Property Status</h5>
          <Card className="border-0 shadow-sm chart-card">
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={propertyPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {propertyPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

    </Container>
  );
};

export default Home;
